/**
 * Sel-2 product gallery (ENCAPSULATED) — Glider.js main carousel
 * Only targets [data-sel-2-gallery] inside the Sel-2 Product Hero section.
 * Glider is loaded on layout theme.sel-2.main.
 */
(function () {
  'use strict';

  var galleries = [];
  var FADE_MS = 280;

  function normalizeAltHandle(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9-]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function isSharedGalleryMedia(media) {
    if (!media) return false;
    if (media.shared === true || media.shared === 'true') return true;
    return String(media.alt || '').trim() === '';
  }

  function mediaMatchesHandle(media, handle) {
    if (!handle) return false;
    var altHandle = normalizeAltHandle(media && media.alt);
    if (!altHandle) return false;
    if (altHandle === handle) return true;
    if (altHandle.indexOf(handle) === 0) {
      var next = altHandle.charAt(handle.length);
      return next === '' || next === '-';
    }
    var idx = altHandle.indexOf(handle);
    if (idx === -1) return false;
    var beforeOk = idx === 0 || altHandle.charAt(idx - 1) === '-';
    var afterIdx = idx + handle.length;
    var afterOk = afterIdx === altHandle.length || altHandle.charAt(afterIdx) === '-';
    return beforeOk && afterOk;
  }

  function isHiddenGalleryMedia(media) {
    var alt = String((media && media.alt) || '').toLowerCase();
    return alt.indexOf('hidden') !== -1 && alt.indexOf('temp-hidden') === -1;
  }

  function withoutHiddenMedia(mediaList) {
    return (mediaList || []).filter(function (m) {
      return !isHiddenGalleryMedia(m);
    });
  }

  function filterMediaByHandle(mediaList, handle) {
    var visible = withoutHiddenMedia(mediaList);
    if (!handle) return [];
    var exact = [];
    var shared = [];
    visible.forEach(function (m) {
      if (isSharedGalleryMedia(m)) {
        shared.push(m);
        return;
      }
      if (mediaMatchesHandle(m, handle)) exact.push(m);
    });
    return exact.concat(shared);
  }

  function hydrateImg(img, options) {
    if (!img || img.getAttribute('data-hydrated') === '1') return;
    var src = img.getAttribute('data-src');
    var srcset = img.getAttribute('data-srcset');
    if (!src && !srcset && img.getAttribute('src')) {
      img.setAttribute('data-hydrated', '1');
      return;
    }
    if (srcset) img.srcset = srcset;
    if (src) img.src = src;
    if (options && options.eager) {
      img.loading = 'eager';
      try {
        img.fetchPriority = 'high';
      } catch (e) {
        /* older browsers */
      }
    }
    img.setAttribute('data-hydrated', '1');
  }

  function whenIdle(fn, timeout) {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(fn, { timeout: timeout || 1200 });
    } else {
      window.setTimeout(fn, timeout || 200);
    }
  }

  function mediaFromEl(el) {
    return {
      id: el.getAttribute('data-media-id'),
      alt: el.getAttribute('data-media-alt') || '',
      shared: el.getAttribute('data-media-shared') === 'true',
      src: el.getAttribute('data-media-src') || '',
      srcset: el.getAttribute('data-media-srcset') || '',
      thumb: el.getAttribute('data-media-thumb') || '',
      thumbSrcset: el.getAttribute('data-media-thumb-srcset') || ''
    };
  }

  function SelGallery(root) {
    this.root = root;
    this.main = root.querySelector('.sel-2-gallery__main');
    this.gliderEl = root.querySelector('[data-sel-2-gallery-glider]');
    this.thumbsWrap = root.querySelector('[data-sel-2-gallery-thumbs]');
    this.thumbsTrack = root.querySelector('[data-sel-2-gallery-thumbs-track]');
    this.prevBtn = root.querySelector('[data-sel-2-gallery-prev]');
    this.nextBtn = root.querySelector('[data-sel-2-gallery-next]');
    this.index = 0;
    this.visible = [];
    this.glider = null;
    this._fadeBusy = false;
    this._onSlideVisible = null;
    this._onAnimated = null;
    this.visibleThumbCount = 4;
    this.filterOnVariantSelect = root.getAttribute('data-filter-on-variant-select') === 'true';
    this.transition = root.getAttribute('data-transition') || 'slide';
    if (this.transition !== 'fade' && this.transition !== 'slide') this.transition = 'slide';
    this.fadeEnabled = this.transition === 'fade';
    this.showArrows = root.getAttribute('data-show-arrows') === 'true';

    var catalogEl = root.querySelector('[data-sel-2-gallery-catalog]');
    this.catalog = [];
    if (catalogEl) {
      try {
        this.catalog = withoutHiddenMedia(JSON.parse(catalogEl.textContent) || []);
      } catch (e) {
        console.error('[sel-2] gallery catalog parse failed', e);
        this.catalog = [];
      }
    }

    this.syncVisibleFromDom();
    this.initGlider();
    this.bindNav();
    this.bindThumbs();
    this.updateNavVisibility();
    this.slideThumbsToActive(false);
    this.hydrateNearbySlides(0, { onlyCurrent: true });
    this.scheduleThumbHydration();

    var self = this;
    if (typeof ResizeObserver !== 'undefined' && this.main) {
      this._ro = new ResizeObserver(function () {
        self.slideThumbsToActive(false);
        if (self.glider && self.glider.refresh) self.glider.refresh(true);
      });
      this._ro.observe(this.main);
      if (this.thumbsWrap) this._ro.observe(this.thumbsWrap);
    } else {
      window.addEventListener('resize', function () {
        self.slideThumbsToActive(false);
      });
    }
  }

  SelGallery.prototype.syncVisibleFromDom = function () {
    var slides = Array.prototype.slice.call(
      this.root.querySelectorAll('[data-sel-2-gallery-slide]')
    );
    if (slides.length) {
      this.visible = slides.map(mediaFromEl);
      return;
    }
    var thumbs = Array.prototype.slice.call(
      this.root.querySelectorAll('[data-sel-2-gallery-thumb]')
    );
    this.visible = thumbs.map(mediaFromEl);
  };

  SelGallery.prototype.isFadeMode = function () {
    return this.transition === 'fade' || this.root.classList.contains('sel-2-gallery--fade');
  };

  SelGallery.prototype.clearGliderCache = function (el) {
    if (!el) return;
    try {
      delete el._glider;
    } catch (e) {
      el._glider = undefined;
    }
  };

  SelGallery.prototype.unbindGliderEvents = function () {
    if (!this.gliderEl) return;
    if (this._onSlideVisible) {
      this.gliderEl.removeEventListener('glider-slide-visible', this._onSlideVisible);
      this._onSlideVisible = null;
    }
    if (this._onAnimated) {
      this.gliderEl.removeEventListener('glider-animated', this._onAnimated);
      this._onAnimated = null;
    }
  };

  SelGallery.prototype.destroyGlider = function () {
    this.unbindGliderEvents();
    if (this.glider && typeof this.glider.destroy === 'function') {
      try {
        this.glider.destroy();
      } catch (e) {
        /* ignore */
      }
    }
    this.clearGliderCache(this.gliderEl);
    this.glider = null;
  };

  SelGallery.prototype.initGlider = function () {
    if (!this.gliderEl || typeof window.Glider !== 'function') return;
    if (!this.visible.length) return;

    this.clearGliderCache(this.gliderEl);
    this.unbindGliderEvents();

    var self = this;
    var fade = this.isFadeMode();

    this.glider = new window.Glider(this.gliderEl, {
      slidesToShow: 1,
      slidesToScroll: 1,
      draggable: this.visible.length > 1,
      dragVelocity: 2.8,
      scrollLock: true,
      scrollLockDelay: 50,
      resizeLock: true,
      duration: fade ? 0.01 : 0.18,
      easing: function (_x, t, b, c, d) {
        return c * (t / d) + b;
      }
    });

    this._onSlideVisible = function (e) {
      var i = e && e.detail && typeof e.detail.slide === 'number' ? e.detail.slide : null;
      if (i == null && self.glider) i = self.glider.slide;
      if (typeof i !== 'number') return;
      self.hydrateNearbySlides(i);
      if (i === self.index) {
        self.updateNavVisibility();
        return;
      }
      self.index = i;
      self.syncThumbActive();
      self.updateNavVisibility();
      self.slideThumbsToActive(true);
    };

    this._onAnimated = function () {
      self._fadeBusy = false;
      if (self.main) self.main.classList.remove('is-fading');
      self.updateNavVisibility();
    };

    this.gliderEl.addEventListener('glider-slide-visible', this._onSlideVisible);
    this.gliderEl.addEventListener('glider-animated', this._onAnimated);

    // Ensure layout after absolute-positioned shell
    requestAnimationFrame(function () {
      if (self.glider && self.glider.refresh) self.glider.refresh(true);
      if (self.main) self.main.classList.remove('is-fading');
      self._fadeBusy = false;
    });
  };

  SelGallery.prototype.bindNav = function () {
    var self = this;
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', function (e) {
        e.preventDefault();
        self.setActive(self.index - 1);
      });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', function (e) {
        e.preventDefault();
        self.setActive(self.index + 1);
      });
    }
  };

  SelGallery.prototype.bindThumbs = function () {
    var self = this;
    var thumbs = this.root.querySelectorAll('[data-sel-2-gallery-thumb]');
    Array.prototype.forEach.call(thumbs, function (btn, i) {
      btn.onclick = function () {
        self.setActive(i);
      };
    });
  };

  SelGallery.prototype.updateNavVisibility = function () {
    if (!this.showArrows) {
      if (this.prevBtn) this.prevBtn.hidden = true;
      if (this.nextBtn) this.nextBtn.hidden = true;
      return;
    }

    var total = this.visible.length;
    if (total <= 1) {
      if (this.prevBtn) this.prevBtn.hidden = true;
      if (this.nextBtn) this.nextBtn.hidden = true;
      return;
    }

    if (this.prevBtn) this.prevBtn.hidden = this.index <= 0;
    if (this.nextBtn) this.nextBtn.hidden = this.index >= total - 1;
  };

  SelGallery.prototype.syncThumbActive = function () {
    var thumbs = this.root.querySelectorAll('[data-sel-2-gallery-thumb]');
    Array.prototype.forEach.call(
      thumbs,
      function (t, n) {
        var active = n === this.index;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-pressed', active ? 'true' : 'false');
      }.bind(this)
    );
  };

  SelGallery.prototype.setActive = function (i) {
    if (!this.visible.length) return;
    var nextIndex;
    if (this.showArrows) {
      nextIndex = Math.max(0, Math.min(i, this.visible.length - 1));
    } else {
      nextIndex = ((i % this.visible.length) + this.visible.length) % this.visible.length;
    }
    if (nextIndex === this.index) {
      this.slideThumbsToActive(true);
      this.updateNavVisibility();
      return;
    }

    var self = this;
    var go = function () {
      self.hydrateNearbySlides(nextIndex);
      self.index = nextIndex;
      self.syncThumbActive();
      self.updateNavVisibility();
      self.slideThumbsToActive(true);
      if (self.glider && typeof self.glider.scrollItem === 'function') {
        self.glider.scrollItem(nextIndex);
      }
    };

    if (this.isFadeMode() && this.main) {
      if (this._fadeBusy) return;
      this._fadeBusy = true;
      this.main.classList.add('is-fading');
      window.setTimeout(function () {
        go();
        window.setTimeout(function () {
          if (self.main) self.main.classList.remove('is-fading');
          self._fadeBusy = false;
        }, FADE_MS);
      }, FADE_MS);
      return;
    }

    go();
  };

  SelGallery.prototype.sizeThumbs = function () {
    if (!this.thumbsWrap || !this.thumbsTrack) return;
    var thumbs = this.thumbsTrack.querySelectorAll('[data-sel-2-gallery-thumb]');
    if (!thumbs.length) return;

    var wrapW = this.thumbsWrap.clientWidth;
    var styles = window.getComputedStyle(this.thumbsTrack);
    var gap = parseFloat(styles.columnGap || styles.gap) || 16;
    var visible = this.visibleThumbCount;
    var width = (wrapW - (visible - 1) * gap) / visible;

    Array.prototype.forEach.call(thumbs, function (thumb) {
      thumb.style.flex = '0 0 ' + width + 'px';
      thumb.style.width = width + 'px';
      thumb.style.maxWidth = width + 'px';
    });
  };

  SelGallery.prototype.slideThumbsToActive = function (smooth) {
    if (!this.thumbsWrap || !this.thumbsTrack) return;
    this.sizeThumbs();

    var thumbs = this.thumbsTrack.querySelectorAll('[data-sel-2-gallery-thumb]');
    var total = thumbs.length;
    if (!total) return;

    if (smooth === false) {
      this.thumbsTrack.style.transition = 'none';
    } else {
      this.thumbsTrack.style.transition = '';
    }

    var visible = this.visibleThumbCount;
    if (total <= visible) {
      this.thumbsTrack.style.transform = 'translate3d(0, 0, 0)';
      if (smooth === false) {
        void this.thumbsTrack.offsetWidth;
        this.thumbsTrack.style.transition = '';
      }
      return;
    }

    var start = Math.max(0, Math.min(this.index - 1, total - visible));
    var styles = window.getComputedStyle(this.thumbsTrack);
    var gap = parseFloat(styles.columnGap || styles.gap) || 16;
    var step = thumbs[0].getBoundingClientRect().width + gap;
    var offset = start * step;
    this.thumbsTrack.style.transform = 'translate3d(' + -offset + 'px, 0, 0)';

    if (smooth === false) {
      void this.thumbsTrack.offsetWidth;
      this.thumbsTrack.style.transition = '';
    }
  };

  SelGallery.prototype.hydrateNearbySlides = function (atIndex, opts) {
    if (!this.gliderEl) return;
    opts = opts || {};
    var slides = this.gliderEl.querySelectorAll('[data-sel-2-gallery-slide]');
    if (!slides.length) return;
    var i = typeof atIndex === 'number' ? atIndex : this.index || 0;
    var radius = opts.onlyCurrent ? 0 : opts.radius != null ? opts.radius : 1;
    var start = Math.max(0, i - radius);
    var end = Math.min(slides.length - 1, i + radius);
    for (var n = start; n <= end; n++) {
      var img = slides[n] && slides[n].querySelector('img.sel-2-gallery__main-img');
      if (!img) continue;
      if (img.getAttribute('src') && !img.getAttribute('data-src')) {
        img.setAttribute('data-hydrated', '1');
        continue;
      }
      hydrateImg(img, { eager: n === i });
    }
  };

  SelGallery.prototype.scheduleThumbHydration = function () {
    var self = this;
    whenIdle(function () {
      self.hydrateThumbs();
      // Warm the next main slide after LCP has had time to win bandwidth
      self.hydrateNearbySlides(self.index || 0);
    }, 1500);
  };

  SelGallery.prototype.hydrateThumbs = function () {
    if (!this.root) return;
    var imgs = this.root.querySelectorAll('.sel-2-gallery__thumb-img[data-src]');
    Array.prototype.forEach.call(imgs, function (img) {
      hydrateImg(img);
    });
  };

  SelGallery.prototype.createSlide = function (media, eager) {
    var slide = document.createElement('div');
    slide.className = 'sel-2-gallery__slide';
    slide.setAttribute('data-sel-2-gallery-slide', '');
    slide.setAttribute('data-media-id', media.id || '');
    slide.setAttribute('data-media-src', media.src || '');
    slide.setAttribute('data-media-srcset', media.srcset || '');
    slide.setAttribute('data-media-alt', media.alt || '');
    slide.setAttribute(
      'data-media-shared',
      media.shared === true || media.shared === 'true' ? 'true' : 'false'
    );
    slide.setAttribute('data-media-thumb', media.thumb || '');
    slide.setAttribute('data-media-thumb-srcset', media.thumbSrcset || '');

    var img = document.createElement('img');
    img.className = 'sel-2-gallery__main-img';
    img.alt = media.alt || '';
    img.sizes = '(min-width: 900px) min(58vw, 720px), 92vw';
    img.decoding = 'async';
    img.width = 1400;
    img.height = 1400;

    if (eager) {
      img.loading = 'eager';
      try {
        img.fetchPriority = 'high';
      } catch (e) {
        /* ignore */
      }
      if (media.srcset) img.srcset = media.srcset;
      img.src = media.src || media.thumb || '';
      img.setAttribute('data-hydrated', '1');
    } else {
      img.loading = 'lazy';
      try {
        img.fetchPriority = 'low';
      } catch (e2) {
        /* ignore */
      }
      if (media.src) img.setAttribute('data-src', media.src);
      if (media.srcset) img.setAttribute('data-srcset', media.srcset);
    }
    slide.appendChild(img);
    return slide;
  };

  /**
   * Fully rebuild the Glider shell. Glider caches instances on ele._glider;
   * reusing the same node after destroy leaves a blank gallery on variant swaps.
   */
  SelGallery.prototype.renderSlides = function (list) {
    if (!this.main) return;

    this.destroyGlider();
    this._fadeBusy = false;
    this.main.classList.remove('is-fading');

    var wrap = document.createElement('div');
    wrap.className = 'sel-2-gallery__glider-wrap glider-contain';
    var track = document.createElement('div');
    track.className = 'glider sel-2-gallery__glider';
    track.setAttribute('data-sel-2-gallery-glider', '');

    var self = this;
    (list || []).forEach(function (media, i) {
      track.appendChild(self.createSlide(media, i === 0));
    });
    wrap.appendChild(track);

    // Keep nav buttons; replace everything else in main
    var keep = [];
    Array.prototype.forEach.call(this.main.children, function (child) {
      if (child.classList && child.classList.contains('sel-2-gallery__nav')) {
        keep.push(child);
      }
    });
    this.main.innerHTML = '';
    this.main.appendChild(wrap);
    keep.forEach(function (btn) {
      self.main.appendChild(btn);
    });
    this.prevBtn = this.main.querySelector('[data-sel-2-gallery-prev]');
    this.nextBtn = this.main.querySelector('[data-sel-2-gallery-next]');

    this.gliderEl = track;
    this.visible = (list || []).slice();
    this.index = 0;

    if (!this.visible.length) {
      wrap.remove();
      var placeholder = document.createElement('div');
      placeholder.className = 'sel-2-gallery__main-media sel-2-gallery__main-media--placeholder';
      placeholder.innerHTML =
        '<svg class="sel-2-gallery__placeholder" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"><rect width="100" height="100" fill="#eee"/></svg>';
      this.main.insertBefore(placeholder, this.main.firstChild);
      this.gliderEl = null;
      return;
    }

    this.initGlider();
    if (this.glider && typeof this.glider.scrollItem === 'function') {
      this.glider.scrollItem(0, true);
    }
    this.hydrateNearbySlides(0, { onlyCurrent: true });
    whenIdle(function () {
      self.hydrateNearbySlides(0);
    }, 800);
  };

  SelGallery.prototype.renderThumbs = function (list) {
    if (!this.thumbsWrap) return;
    if (!list || !list.length) {
      this.thumbsWrap.hidden = true;
      this.thumbsWrap.innerHTML = '';
      this.thumbsTrack = null;
      return;
    }
    this.thumbsWrap.hidden = list.length < 2;

    var track = document.createElement('div');
    track.className = 'sel-2-gallery__thumbs-track';
    track.setAttribute('role', 'list');
    track.setAttribute('data-sel-2-gallery-thumbs-track', '');

    var self = this;
    list.forEach(function (media, i) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'sel-2-gallery__thumb' + (i === 0 ? ' is-active' : '');
      btn.setAttribute('role', 'listitem');
      btn.setAttribute('data-sel-2-gallery-thumb', '');
      btn.setAttribute('data-media-id', media.id || '');
      btn.setAttribute('data-media-src', media.src || '');
      btn.setAttribute('data-media-srcset', media.srcset || '');
      btn.setAttribute('data-media-alt', media.alt || '');
      btn.setAttribute(
        'data-media-shared',
        media.shared === true || media.shared === 'true' ? 'true' : 'false'
      );
      btn.setAttribute('data-media-thumb', media.thumb || '');
      btn.setAttribute('data-media-thumb-srcset', media.thumbSrcset || '');
      btn.setAttribute('aria-label', 'View image ' + (i + 1));
      btn.setAttribute('aria-pressed', i === 0 ? 'true' : 'false');

      var img = document.createElement('img');
      img.className = 'sel-2-gallery__thumb-img';
      img.alt = media.alt || '';
      img.loading = 'lazy';
      img.decoding = 'async';
      img.sizes = '(min-width: 900px) 180px, 28vw';
      img.width = 800;
      img.height = 800;
      try {
        img.fetchPriority = 'low';
      } catch (e) {
        /* ignore */
      }
      if (media.thumb) img.setAttribute('data-src', media.thumb);
      if (media.thumbSrcset) img.setAttribute('data-srcset', media.thumbSrcset);
      btn.appendChild(img);

      btn.addEventListener('click', function () {
        self.setActive(i);
      });
      track.appendChild(btn);
    });

    this.thumbsWrap.innerHTML = '';
    this.thumbsWrap.appendChild(track);
    this.thumbsTrack = track;
    this.slideThumbsToActive(false);
    this.updateNavVisibility();
    this.scheduleThumbHydration();
  };

  SelGallery.prototype.applyFilter = function (state) {
    if (!state || !this.filterOnVariantSelect) return;
    var handle = state.variant_handle ? String(state.variant_handle) : '';
    if (!handle) return;

    var next = filterMediaByHandle(this.catalog, handle);

    this.isVariantFiltered = true;
    this.activeVariantHandle = handle;
    this.index = 0;
    this.root.classList.add('sel-2-gallery--variant-filtered');
    this.root.setAttribute('data-active-variant-handle', handle);

    this.renderSlides(next);
    this.renderThumbs(next);

    var self = this;
    requestAnimationFrame(function () {
      self.syncThumbActive();
      self.updateNavVisibility();
      self.slideThumbsToActive(false);
      if (self.glider && typeof self.glider.scrollItem === 'function') {
        self.glider.scrollItem(0, true);
      }
      self.hydrateNearbySlides(0, { onlyCurrent: true });
    });
  };

  function initGalleries(scope) {
    var roots = (scope || document).querySelectorAll('[data-sel-2-gallery]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.selGalleryReady === 'true') return;
      if (typeof window.Glider !== 'function') return;
      root.dataset.selGalleryReady = 'true';
      galleries.push(new SelGallery(root));
    });
  }

  document.addEventListener('sel-2:variant-change', function (e) {
    var state = e && e.detail ? e.detail : null;
    galleries.forEach(function (g) {
      g.applyFilter(state);
    });
  });

  function boot() {
    initGalleries();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.addEventListener('load', boot);
  setTimeout(boot, 300);

  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target || document;
    Array.prototype.forEach.call(section.querySelectorAll('[data-sel-2-gallery]'), function (root) {
      root.dataset.selGalleryReady = 'false';
    });
    galleries = galleries.filter(function (g) {
      return document.body.contains(g.root);
    });
    initGalleries(section);
  });
})();
